/**
 * Archive-board confirmation (FRIDAY-44).
 *
 * One dialog for both entry points: the board header's "Archive board…" menu
 * item and the danger zone on the board settings screen. It owns the whole
 * write so the two cannot drift apart:
 *
 *   - Cancel, Esc and the scrim close it without a request;
 *   - while the DELETE is in flight every control is disabled;
 *   - a failure keeps the dialog open with the server message and a
 *     "Try again" button;
 *   - success toasts and moves to the workspace overview, because the board
 *     route itself no longer resolves once the board is archived.
 *
 * The server stays the authority on who may archive (project lead or
 * workspace owner); callers only decide whether to offer the action.
 */

import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { ApiError } from '@/lib/api'
import { ConfirmDialog } from '@/components/feedback'
import { toast } from '@/features/feedback/toast'
import { useArchiveProject } from '@/features/projects/board'

export interface ArchiveBoardDialogProps {
  open: boolean
  onClose: () => void
  wsId: string
  project: { id: string; key: string; name: string }
}

export function ArchiveBoardDialog({ open, onClose, wsId, project }: ArchiveBoardDialogProps) {
  const archive = useArchiveProject(wsId, project.id)
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const busy = archive.isPending

  // Cleared on close, so a reopened dialog never shows the last attempt's
  // error, not even for one frame.
  function close() {
    setError(null)
    onClose()
  }

  async function confirm() {
    if (busy) return
    setError(null)
    try {
      await archive.mutateAsync()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not archive the board. Check your connection.')
      return
    }
    close()
    toast(`Board "${project.name}" archived`, { kind: 'success' })
    // The board is gone from active navigation, so any unsaved settings
    // draft is moot: skip the unsaved-changes prompt for this one move.
    void navigate({ to: '/w/$wsId', params: { wsId }, ignoreBlocker: true })
  }

  return (
    <ConfirmDialog
      open={open}
      title={`Archive "${project.name}"?`}
      body={
        <div className="text-surface-600 dark:text-surface-300 space-y-2 text-sm">
          <p>
            {project.name} ({project.key}) leaves the sidebar, the workspace overview and the command
            palette. Tasks, columns and settings are kept.
          </p>
          <p>
            You can restore it later from{' '}
            <Link to="/me/archive" className="text-brand-600 dark:text-brand-300 underline">
              Archive
            </Link>
            .
          </p>
          {error && (
            <p role="alert" className="text-red-600 dark:text-red-300">
              {error}
            </p>
          )}
        </div>
      }
      confirmLabel={error ? 'Try again' : 'Archive board'}
      destructive
      busy={busy}
      onClose={() => {
        if (!busy) close()
      }}
      onConfirm={() => void confirm()}
    />
  )
}
